'use client';

import { Reveal } from '@/components/products/Reveal';
import { researchPillars, commercializationPath, exportCapability } from '@/lib/corporate-content';

/**
 * R&D, commercialization and export capability — the three things a funder
 * reads a company site for, kept factual: every claim here is either a
 * description of engineering already in the shipped products, or a stated
 * intent labelled as such.
 *
 * Layout family: two-column reading spread, then a stats band. No cards.
 */
export function ResearchSection() {
  return (
    <section
      className="self-styled relative z-20 border-b border-white/[0.07]"
      style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
      aria-labelledby="research-heading"
    >
      <div className="content-padding">
        {/* ── Research ── */}
        <div className="pb-6 pt-24 md:pt-32">
          <Reveal>
            <span className="eyebrow">Research and development</span>
            <h2
              id="research-heading"
              className="font-six-caps mt-6 max-w-5xl uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}
            >
              Engineering problems we work on
            </h2>
            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              These are practical problems we have encountered while building the
              products: language quality, safe automation, reliable answers and
              software that keeps working on weak infrastructure.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-x-16 md:grid-cols-2">
          {researchPillars.map((pillar, i) => (
            <Reveal key={pillar.index} delay={(i % 2) * 80}>
              <div className="flex flex-col gap-4 border-t border-white/10 py-9 md:py-11">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[var(--color-accent)]">
                    {pillar.index}
                  </span>
                  <h3 className="text-lg font-medium leading-snug text-white md:text-xl">
                    {pillar.title}
                  </h3>
                </div>
                <p className="max-w-xl text-sm font-light leading-relaxed text-white/55 md:text-[15px]">
                  {pillar.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ── Commercialization path ── */}
        <div className="border-t border-white/10 pb-4 pt-20 md:pt-24">
          <Reveal>
            <span className="eyebrow">Commercialization</span>
            <h2
              className="font-six-caps mt-6 max-w-4xl uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
            >
              How a build becomes a product
            </h2>
          </Reveal>
        </div>

        <ol className="grid grid-cols-1 gap-px pb-20 sm:grid-cols-2 lg:grid-cols-4" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
          {commercializationPath.map((step, i) => (
            <li key={step.index} className="flex" style={{ backgroundColor: '#0a0a0a' }}>
              <Reveal delay={i * 70} className="w-full">
                <div className="flex h-full flex-col gap-4 p-6 md:p-8">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/35">
                    {step.index}
                  </span>
                  <h3 className="text-base font-medium leading-snug text-white">
                    {step.title}
                  </h3>
                  <p className="text-sm font-light leading-relaxed text-white/55">
                    {step.body}
                  </p>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>

        {/* ── Export capability ── */}
        <div className="border-t border-white/10 pb-24 pt-20 md:pt-24">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">{exportCapability.eyebrow}</span>
              <h2
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
              >
                {exportCapability.title}
              </h2>
            </Reveal>
            <Reveal delay={90}>
              <p className="max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
                {exportCapability.body}
              </p>
            </Reveal>
          </div>

          <dl className="mt-14 grid grid-cols-1 gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
            {exportCapability.facts.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 60}>
                <div className="flex flex-col gap-3 border-t border-white/10 py-7">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                    {fact.label}
                  </dt>
                  <dd className="text-base font-light leading-snug text-white/85">
                    {fact.value}
                  </dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
